<template>
    <div class="table-container w-full">
        <div class="mt-1 flex justify-between lg:flex-row flex-col">
            <div class="w-auto lg:pr-2">
                <a-input-search
                    data-cy="user-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div class="lg:w-1/3 w-full lg:pr-2" v-if="showCompanyFilter" data-cy="users-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    @change="handleCompanyFilterChange"
                    :allowClear="true"
                >
                    <a-select-option v-for="(value, index) in company_list" :key="index" :value="index">
                        {{ value.company_name }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="lg:w-1/3 w-full pt-4 lg:pt-0 lg:pr-2" v-if="showAssociationFilter" data-cy="users-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    :filter-option="false"
                    :not-found-content="null"
                    @search="handleFilterSearch"
                    @change="handleAssociationFilterChange"
                    :allowClear="true"
                >
                    <a-select-opt-group
                        v-if="
                            association_list.some((value) =>
                                value.name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Association"
                    >
                        <template v-for="(value, index) in association_list">
                            <a-select-option
                                v-if="value.name.toLowerCase().includes(filter_value.toLowerCase())"
                                :key="`${index}-association`"
                                :value="`${index}-association`"
                            >
                                {{ value.name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                    <a-select-opt-group
                        v-if="
                            company_association_list.some(
                                (value) =>
                                    value.association_code.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Company"
                    >
                        <template v-for="(value, index) in company_association_list">
                            <a-select-option
                                v-if="
                                    value.association_code.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name.toLowerCase().includes(filter_value.toLowerCase())
                                "
                                :key="`${index}-company`"
                                :value="`${index}-company`"
                            >
                                {{ value.company_name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                </a-select>
            </div>
            <div class="w-auto pt-4 lg:pt-0" data-cy="users-date-field">
                <ui-date-range-picker-double v-model="expiry_date_range" @changeDates="handleDateFilterChange" />
            </div>
            <div class="pt-4 lg:pt-0">
                <a-dropdown :trigger="['click']" v-if="recordsSelected">
                    <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                        {{ $t('user.table.actions.actions') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onActionButtons">
                            <a-menu-item key="activate_user" data-cy="actions-activate-user">
                                {{ $t('user.table.actions.activate') }}
                            </a-menu-item>
                            <a-menu-item key="deactivate_user" data-cy="actions-deactivate-user">
                                {{ $t('user.table.actions.deactivate') }}
                            </a-menu-item>
                            <a-menu-divider />
                            <a-menu-item
                                v-if="!multipleRecordsSelected"
                                key="change_password"
                                data-cy="actions-change-password"
                            >
                                {{ $t('user.table.actions.change_password') }}
                            </a-menu-item>
                            <a-menu-item key="reset_password" data-cy="actions-reset-password">
                                {{ $t('user.table.actions.reset_password') }}
                            </a-menu-item>
                            <a-menu-item key="reset_attempt" data-cy="actions-reset-attempt">
                                {{ $t('user.table.actions.reset_login') }}
                            </a-menu-item>
                            <a-menu-item key="reset_expiry_date" data-cy="actions-reset-date">
                                {{ $t('user.table.actions.reset_expiry_date') }}
                            </a-menu-item>
                            <a-menu-divider />
                            <a-menu-item
                                key="export_users"
                                data-cy="actions-export-selected-users"
                                v-if="is_admin_section"
                            >
                                {{ $t('user.table.actions.export') }}
                            </a-menu-item>
                            <a-menu-item key="open_email_modal" data-cy="actions-email-user">
                                {{ $t('user.table.actions.email_users') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
                <a-button data-cy="user-create-button" type="primary" @click="onAdd">{{
                    $t('user.table.actions.create')
                }}</a-button>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full user-table">
                <a-table
                    data-cy="user-table"
                    :columns="
                        is_association_section ? getAssociationColumns : is_admin_section ? getAdminColumns : getColumns
                    "
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    :rowSelection="{
                        selectedRowKeys: selected_row_keys,
                        onChange: onSelectChange,
                        selections: selections,
                    }"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('user.table.actions.edit')">
                                    <a-button
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'edit', record.name)"
                                        :disabled="recordsSelected || select_all_users"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('user.table.actions.logs')">
                                    <a-button
                                        @click="showAuditLogs(record.id)"
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        :disabled="recordsSelected || select_all_users"
                                    >
                                        <LineChartOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('user.table.actions.activate')"
                                    v-if="record.is_active == 0"
                                >
                                    <a-button
                                        class="text-default-green hover:text-default-green focus:text-default-green"
                                        @click="onTableAction(record.id, 'activate', record.name)"
                                        :disabled="recordsSelected || select_all_users"
                                    >
                                        <CheckSquareOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('user.table.actions.deactivate')"
                                    v-if="record.is_active == 1"
                                >
                                    <a-button
                                        class="text-default-red hover:text-default-red focus:text-default-red"
                                        @click="onTableAction(record.id, 'deactivate', record.name)"
                                        :disabled="recordsSelected || select_all_users"
                                    >
                                        <CloseSquareOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('user.table.actions.change_password')">
                                    <a-button
                                        class="text-default-gray hover:text-default-gray focus:text-default-gray"
                                        @click="onTableAction(record.id, 'change_password', record.name)"
                                        :disabled="recordsSelected || select_all_users"
                                    >
                                        <UnlockOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('user.table.actions.reset_password')">
                                    <a-button
                                        class="text-default-gray hover:text-default-gray focus:text-default-gray"
                                        @click="onTableAction(record.id, 'reset_password', record.name)"
                                        :disabled="recordsSelected || select_all_users"
                                    >
                                        <ReloadOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('user.table.actions.reset_login')">
                                    <a-button
                                        class="text-default-gray hover:text-default-gray focus:text-default-gray"
                                        @click="onTableAction(record.id, 'reset_attempts', record.name)"
                                        :disabled="recordsSelected || select_all_users"
                                    >
                                        <HistoryOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'group_code'">
                            {{ record.association_code }} : {{ record.company_code }}
                        </template>
                        <template v-else-if="column.key === 'roles'">
                            {{ getRoles(text) }}
                        </template>
                        <template v-else-if="column.key === 'is_active'">
                            <span
                                class="font-semibold text-default-red"
                                data-cy="user-status-inactive"
                                v-if="text === 0"
                            >
                                {{ $t('user.table.columns.inactive') }}
                            </span>
                            <span
                                class="font-semibold text-default-green"
                                data-cy="user-status-active"
                                v-if="text === 1"
                            >
                                {{ $t('user.table.columns.active') }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'expiry_date'">
                            <span class="whitespace-nowrap">
                                {{ text }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'is_tfa_enabled'">
                            <span class="font-semibold text-default-red" v-if="text === 0">
                                {{ $t('user.table.columns.disabled') }}
                            </span>
                            <span class="font-semibold text-default-green" v-if="text === 1">
                                {{ $t('user.table.columns.enabled') }}
                            </span>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
        <a-modal
            :class="'epic' + moduleStateLink"
            data-cy="audit-logs-modal"
            :title="$t('user.audit_logs.audit_logs_modal_title')"
            :destroyOnClose="true"
            v-model:open="modal_visible"
            :footer="null"
            width="auto"
            :dialog-style="{ padding: '0 20px', maxWidth: '1536px' }"
            ><div class="form-container">
                <audit-log-table :user_id="user_id" />
            </div>
        </a-modal>
    </div>
</template>

<script>
import UiDateRangePickerDouble from '@/components/ui/DateRangePickerDouble.vue';
import AuditLogTable from '@/components/tables/AuditLogs.vue';
import {
    CheckSquareOutlined,
    CloseSquareOutlined,
    DownOutlined,
    EditOutlined,
    HistoryOutlined,
    LineChartOutlined,
    ReloadOutlined,
    UnlockOutlined,
} from '@ant-design/icons-vue';

export default {
    components: {
        UiDateRangePickerDouble,
        AuditLogTable,
        CheckSquareOutlined,
        CloseSquareOutlined,
        DownOutlined,
        EditOutlined,
        HistoryOutlined,
        LineChartOutlined,
        ReloadOutlined,
        UnlockOutlined,
    },
    data() {
        return {
            data: [],
            search_value: null,
            expiry_date_range: {
                start_date: null,
                end_date: null,
            },
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            company_list: [],
            company_association_list: [],
            association_list: [],
            select_all_users: false,
            user_id: '',
            filter_value: '',
            modal_visible: false,
        };
    },
    props: {
        company_id: {
            type: String,
            default: '',
        },
        association_id: {
            type: String,
            default: '',
        },
        is_admin_section: {
            type: Boolean,
            default: false,
        },
        is_association_section: {
            type: Boolean,
            default: false,
        },
        selected_row_keys: {
            type: Array,
            default: [],
        },
        active_users_only: {
            type: Boolean,
            default: true,
        },
    },
    mounted() {
        this.fetch({
            active_users_only: this.active_users_only,
        });

        if (this.fetch_association_list) {
            this.$http
                .get('/api/admin/list-companies-by-associations')
                .then((response) => {
                    this.company_association_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCAL04' }),
                    });
                });
            this.$http
                .get('/api/admin/list-associations')
                .then((response) => {
                    this.association_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCAL03' }),
                    });
                });
        }

        if (this.fetch_company_list) {
            this.$http
                .get('/api/admin/list-companies', {
                    params: {
                        association_id: this.$store.getters['user/currentAssociationId'],
                    },
                })
                .then((response) => {
                    this.company_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCL01' }),
                    });
                });
        }
    },
    computed: {
        getColumns() {
            return [
                {
                    title: () => {
                        if (this.select_all_users) {
                            return this.$t('globals.tables.users.headers.selected_count') + this.pagination.total;
                        } else {
                            if (this.selected_row_keys.length > 0) {
                                return (
                                    this.$t('globals.tables.users.headers.selected_count') +
                                    this.selected_row_keys.length
                                );
                            } else {
                                return '';
                            }
                        }
                    },
                    key: 'actions',
                    width: 155,
                },
                {
                    title: this.$t('globals.tables.users.headers.name'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.roles'),
                    key: 'roles',
                    dataIndex: 'roles',
                    sorter: false,
                },
                {
                    title: this.$t('globals.tables.users.headers.email'),
                    key: 'email',
                    dataIndex: 'email',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.status'),
                    key: 'is_active',
                    dataIndex: 'is_active',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.tfa'),
                    key: 'is_tfa_enabled',
                    dataIndex: 'is_tfa_enabled',
                    sorter: true,
                },
            ];
        },
        getAdminColumns() {
            return [
                {
                    title: () => {
                        if (this.select_all_users) {
                            return this.$t('globals.tables.users.headers.selected_count') + this.pagination.total;
                        } else {
                            if (this.selected_row_keys.length > 0) {
                                return (
                                    this.$t('globals.tables.users.headers.selected_count') +
                                    this.selected_row_keys.length
                                );
                            } else {
                                return '';
                            }
                        }
                    },
                    key: 'actions',
                    width: 155,
                },
                {
                    key: 'group_code',
                    title: this.$t('globals.tables.users.headers.group_code'),
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.name'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.roles'),
                    key: 'roles',
                    dataIndex: 'roles',
                    sorter: false,
                },
                {
                    title: this.$t('globals.tables.users.headers.email'),
                    key: 'email',
                    dataIndex: 'email',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.status'),
                    key: 'is_active',
                    dataIndex: 'is_active',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.expiry_date'),
                    key: 'expiry_date',
                    dataIndex: 'expiry_date',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.tfa'),
                    key: 'is_tfa_enabled',
                    dataIndex: 'is_tfa_enabled',
                    sorter: true,
                },
            ];
        },
        getAssociationColumns() {
            return [
                {
                    title: () => {
                        if (this.select_all_users) {
                            return this.$t('globals.tables.users.headers.selected_count') + this.pagination.total;
                        } else {
                            if (this.selected_row_keys.length > 0) {
                                return (
                                    this.$t('globals.tables.users.headers.selected_count') +
                                    this.selected_row_keys.length
                                );
                            } else {
                                return '';
                            }
                        }
                    },
                    key: 'actions',
                    width: 155,
                },
                {
                    key: 'company_code',
                    title: this.$t('globals.tables.users.headers.company_code'),
                    key: 'company_code',
                    dataIndex: 'company_code',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.name'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.roles'),
                    key: 'roles',
                    dataIndex: 'roles',
                    sorter: false,
                },
                {
                    title: this.$t('globals.tables.users.headers.email'),
                    key: 'email',
                    dataIndex: 'email',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.status'),
                    key: 'is_active',
                    dataIndex: 'is_active',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.expiry_date'),
                    key: 'expiry_date',
                    dataIndex: 'expiry_date',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.users.headers.tfa'),
                    key: 'is_tfa_enabled',
                    dataIndex: 'is_tfa_enabled',
                    sorter: true,
                },
            ];
        },
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'claim';
        },
        showCompanyFilter() {
            if (this.association_id !== '') {
                this.fetch_company_list = true;

                return true;
            } else {
                return false;
            }
        },
        showAssociationFilter() {
            if (this.is_admin_section) {
                this.fetch_association_list = true;

                return true;
            } else {
                return false;
            }
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
        multipleRecordsSelected() {
            return this.selected_row_keys.length > 1;
        },
        selections() {
            return [
                {
                    key: 'all-data',
                    text: this.select_all_users ? 'UnSelect All Total Users' : 'Select All Total Users',
                    onSelect: (changableRowKeys) => {
                        this.select_all_users = !this.select_all_users;
                        let updated_selected_rows = [];
                        if (this.select_all_users) {
                            if (this.data.length != 0) {
                                this.data.forEach((record) => {
                                    updated_selected_rows.push(record.id);
                                });
                            }
                            this.$emit('onSelectChange', updated_selected_rows);
                        } else {
                            this.$emit('onSelectChange', []);
                        }
                    },
                },
            ];
        },
    },
    methods: {
        getRoles(roles) {
            return _.join(roles, ', ');
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        fetch(params = {}) {
            this.loading = true;
            let start_date = null;
            let end_date = null;

            if (params.expiry_date_range) {
                start_date = params.expiry_date_range.start_date
                    ? params.expiry_date_range.start_date.format('YYYY-MM-DD')
                    : null;
                end_date = params.expiry_date_range.end_date
                    ? params.expiry_date_range.end_date.format('YYYY-MM-DD')
                    : null;

                delete params.expiry_date_range;
            }

            this.params = {
                company_id: this.params.company_id ? this.params.company_id : null,
                association_id: this.params.association_id ? this.params.association_id : null,
                search_value: this.params.search_value ? this.params.search_value : null,
                start_date: start_date,
                end_date: end_date,
                active_users_only: this.params.active_users_only ? this.params.active_users_only : null,
                ...params,
            };

            let url = '';

            if (this.is_admin_section) {
                url = '/api/users';
            } else if (this.is_association_section) {
                url = '/api/associations/' + this.association_id + '/users';
            } else {
                url = '/api/companies/' + this.company_id + '/users';
            }

            this.$http
                .get(url, {
                    params: {
                        ...this.params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                    let updated_selected_rows = [];
                    if (this.select_all_users) {
                        this.data.forEach((record) => {
                            updated_selected_rows.push(record.id);
                        });
                        this.$emit('onSelectChange', updated_selected_rows);
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVUSF01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.select_all_users = false;
                this.$emit('onSelectChange', []);

                this.fetch({
                    search_value: this.search_value,
                });
                this.pagination.current = 1;
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        handleDateFilterChange() {
            this.select_all_users = false;
            this.$emit('onSelectChange', []);

            this.fetch({
                expiry_date_range: this.expiry_date_range,
            });
            this.pagination.current = 1;
        },
        handleCompanyFilterChange(value) {
            let company_id = null;
            if (value == undefined) {
                this.fetch_company_list = true;
            } else {
                this.fetch_company_list = null;
                company_id = this.company_list[value].company_id;
            }

            this.select_all_users = false;
            this.$emit('onSelectChange', []);

            this.fetch({
                search_value: this.search_value,
                company_id: company_id,
            });
            this.pagination.current = 1;
        },
        handleAssociationFilterChange(value) {
            let company_id = null;
            let association_id = null;

            if (value == undefined) {
                this.fetch_association_list = true;
            } else {
                this.fetch_association_list = null;
                if (value.includes('company')) {
                    company_id = this.company_association_list[parseInt(value)].company_id;
                    association_id = this.company_association_list[parseInt(value)].association_id;
                } else {
                    association_id = this.association_list[parseInt(value)].id;
                }
            }

            this.select_all_users = false;
            this.$emit('onSelectChange', []);

            this.fetch({
                search_value: this.search_value,
                company_id: company_id,
                association_id: association_id,
            });
            this.pagination.current = 1;
        },
        handleFilterSearch(value) {
            this.filter_value = value;
        },
        filterOption(input, option) {
            return option.children()[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        onSelectChange(selected_row_keys, selected_rows) {
            if (this.select_all_users) {
                this.select_all_users = false;
                this.$emit('onSelectChange', []);
            } else {
                this.$emit('onSelectChange', selected_row_keys);
            }
        },
        onTableAction(id, key, name) {
            this.$emit('onTableAction', {
                id: id,
                key: key,
                name: name,
            });
        },
        onAdd() {
            this.$emit('onTableAction', {
                id: null,
                key: 'create',
            });
        },
        onActionButtons(event) {
            this.$emit('onActionButtons', event, 'default');
        },
        showAuditLogs(user_id) {
            this.user_id = user_id;
            this.modal_visible = true;
        },
    },
};
</script>
